import { useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { Footer, MobileDock, Navbar } from './components/Layout';
import { Loader, Page, Toasts } from './components/UI';
import Landing from './pages/Landing';
import { Login, Register } from './pages/Auth';
import Home from './pages/Home';
import Matches from './pages/Matches';
import Search from './pages/Search';
import Profile from './pages/Profile';
import Interests from './pages/Interests';
import Messages from './pages/Messages';
import { Shortlist, Visitors } from './pages/Lists';
import Kundli from './pages/Kundli';
import Premium from './pages/Premium';
import EditProfile from './pages/EditProfile';
import Preferences from './pages/Preferences';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import { Help, NotFound, Stories } from './pages/Static';

// Route guards decide once, when their route mounts. Reacting to later session
// changes would swap a page that is animating out for <Navigate>, which leaves
// AnimatePresence waiting on an exit that never finishes. Login/logout already
// navigate explicitly, and an expired session reloads to /login (AppContext).
function Private({ children }) {
  const { user, booting } = useApp();
  const location = useLocation();
  const hadUser = useRef(false);
  if (booting) return <Loader label="Opening your lounge…" />;
  if (user) {
    hadUser.current = true;
    return children;
  }
  return hadUser.current ? <Page /> : <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
}

function GuestOnly({ children }) {
  const { user, booting } = useApp();
  const decided = useRef(null);
  if (booting) return <Loader label="Just a moment…" />;
  if (decided.current === null) decided.current = !!user;
  return decided.current ? <Navigate to="/home" replace /> : children;
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  const p = (el) => <Private>{el}</Private>;
  // Switching chat threads stays on one page; other param changes animate as new pages.
  const [, first, second = ''] = location.pathname.split('/');
  const pageKey = first === 'messages' ? first : first + second;
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={pageKey}>
        <Route path="/" element={<GuestOnly><Landing /></GuestOnly>} />
        <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
        <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
        <Route path="/stories" element={<Stories />} />
        <Route path="/help" element={<Help />} />
        <Route path="/premium" element={<Premium />} />

        <Route path="/home" element={p(<Home />)} />
        <Route path="/matches" element={p(<Matches />)} />
        <Route path="/search" element={p(<Search />)} />
        <Route path="/profile/:id" element={p(<Profile />)} />
        <Route path="/interests" element={p(<Interests />)} />
        <Route path="/messages" element={p(<Messages />)} />
        <Route path="/messages/:id" element={p(<Messages />)} />
        <Route path="/shortlist" element={p(<Shortlist />)} />
        <Route path="/visitors" element={p(<Visitors />)} />
        <Route path="/kundli" element={p(<Kundli />)} />
        <Route path="/kundli/:id" element={p(<Kundli />)} />
        <Route path="/me/edit" element={p(<EditProfile />)} />
        <Route path="/me/preferences" element={p(<Preferences />)} />
        <Route path="/settings" element={p(<Settings />)} />
        <Route path="/notifications" element={p(<Notifications />)} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const bare = pathname.startsWith('/messages');
  return (
    <>
      <ScrollTop />
      <Navbar />
      <AnimatedRoutes />
      {!bare && <Footer />}
      <MobileDock />
      <Toasts />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppProvider>
        <Shell />
      </AppProvider>
    </BrowserRouter>
  );
}
